import { useState, useMemo, useCallback } from "react";
import {
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  useReactTable,
  type SortingState,
  Row,
} from "@tanstack/react-table";
import { useGetAssignmentsOfStudentFromTeacher } from "@packages/api/madlenAPI";
import { useTranslation } from "react-i18next";
import { StudentAssignmentResponseDto } from "@packages/api/madlenAPI.schemas";
import { Eye, Newspaper } from "lucide-react";
import { createTruncatedCell, GenericTable } from "@core/table";
import TableActionLink from "@packages/components/table-action-link";
import { CourseData } from "@packages/constant/CourseData";
import Text from "@core/text";

interface SingleStudentAssignmentsTableProps {
  id: number;
}

export const SingleStudentAssignmentsTable = ({
  id,
}: SingleStudentAssignmentsTableProps) => {
  const { t } = useTranslation();

  const [sorting, setSorting] = useState<SortingState>([
    {
      id: "assignmentName",
      desc: false,
    },
  ]);

  const {
    data: assignments,
    isLoading: assignmentsLoading,
    error: assignmentsError,
  } = useGetAssignmentsOfStudentFromTeacher(Number(id));

  const tableData = useMemo(
    () => assignments?.data ?? [],
    [JSON.stringify(assignments)],
  );

  const columns = useMemo(
    () => [
      {
        header: t("assignments.table.name"),
        accessorKey: "assignmentName",
        cell: createTruncatedCell(60, true),
      },
      {
        header: t("assignments.table.course"),
        accessorKey: "courseType",
        cell: (info: any) => {
          return t(
            `courseType.${CourseData[
              info.getValue() as keyof typeof CourseData
            ]?.name(info.row.original.assignmentGrade ?? 8)}`,
          );
        },
      },
      {
        header: t("assignments.table.type"),
        accessorKey: "assignmentType",
        cell: (info: any) => t(`${info.getValue()}`),
      },
      {
        header: t("assignments.table.status.status"),
        accessorKey: "studentAssignmentStatus",
        cell: (info: any) => {
          const status = info.getValue();
          const statusConfig = {
            PENDING: {
              bgClass:
                "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200",
              text: t("assignments.table.status.pending"),
            },
            IN_PROGRESS: {
              bgClass:
                "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200",
              text: t("assignments.table.status.inProgress"),
            },
            FINISHED: {
              bgClass:
                "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200",
              text: t("assignments.table.status.completed"),
            },
            DID_NOT_START: {
              bgClass:
                "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200",
              text: t("assignments.table.status.didNotStart"),
            },
          };

          return (
            <Text
              variant="span"
              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-sm font-medium ${statusConfig[(status as keyof typeof statusConfig) ?? "PENDING"].bgClass}`}
            >
              {
                statusConfig[(status as keyof typeof statusConfig) ?? "PENDING"]
                  .text
              }
            </Text>
          );
        },
      },
      {
        header: t("assignments.table.score"),
        accessorKey: "studentTotalPoints",
        cell: (info: any) =>
          `${info.getValue() ?? 0}/${info?.row?.original?.totalPointsOfAssignment ?? 0}`,
      },
      {
        header: t("assignments.table.progress"),
        accessorKey: "studentProgressInTheAssignment",
        cell: (info: any) => `${info.getValue() ?? 0}%`,
      },
      {
        header: t("assignments.table.actions.title"),
        accessorKey: "assignmentId",
        cell: (info) => (
          <div className="flex items-center gap-x-4">
            <TableActionLink
              icon={<Eye size={24} />}
              label={t("assignments.table.actions.viewAssignment")}
              to={`/assignments/${info.getValue()}`}
            />
            <TableActionLink
              icon={
                <Newspaper
                  className={
                    info.row.original.studentAssignmentStatus !== "FINISHED"
                      ? "opacity-50 cursor-not-allowed"
                      : ""
                  }
                  size={24}
                />
              }
              label={
                info.row.original.studentAssignmentStatus !== "FINISHED"
                  ? t("assignments.table.actions.cannotView")
                  : t("assignments.table.actions.viewStudentAssignment")
              }
              to={`/students/${id}/assignments/${info.getValue()}`}
              disabled={
                info.row.original.studentAssignmentStatus !== "FINISHED"
              }
            />
          </div>
        ),
      },
    ],
    [t],
  );

  const table = useReactTable({
    data: tableData,
    columns,
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    globalFilterFn: useCallback(
      (
        row: Row<StudentAssignmentResponseDto>,
        columnId: string,
        filterValue: string,
      ) => {
        const value = row.getValue(columnId);
        if (typeof value === "string") {
          return value.toLowerCase().includes(filterValue.toLowerCase());
        }
        if (typeof value === "number") {
          return value.toString().includes(filterValue);
        }
        return false;
      },
      [],
    ),
    enableSorting: true,
  });

  return (
    <GenericTable
      table={table}
      isLoading={assignmentsLoading}
      error={assignmentsError}
      isEmpty={!assignments?.data?.length}
    />
  );
};
